import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { EmptyState, ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { useAutoLoadMore } from '@/hooks/useAutoLoadMore';
import { ansEntityParams, fetchAnsEntities } from '@/services/ans';

const PAGE_SIZE = 50;

/**
 * The ANS operator directory: every operator the penalties ledger and the two
 * registries name, BY NAME, never by count. An operator that appears only
 * through a registry carries no penalty in the snapshot competence; it is
 * listed so a search finds it, and the row says so.
 */
export function AnsEntitiesPage() {
  const { t, i18n } = useTranslation(['ans', 'common']);
  const [seed] = useSearchParams();
  const [draft, setDraft] = useState(seed.get('query') ?? '');
  const [query, setQuery] = useState<string | null>(seed.get('query') || null);
  const [onlyPenalised, setOnlyPenalised] = useState(seed.get('only_penalised') === 'true');

  const filters = { query, only_penalised: onlyPenalised || null };

  const {
    data: paged,
    isPending,
    error,
    refetch,
    isPlaceholderData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['ans', 'entities', filters],
    queryFn: ({ pageParam }) =>
      fetchAnsEntities({ ...filters, limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, page) => n + page.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    placeholderData: keepPreviousData,
  });

  const data = paged?.pages[0];
  const items = paged?.pages.flatMap((page) => page.items) ?? [];
  const { sentinelRef, scrollRef, loadMore } = useAutoLoadMore({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('ans:entities.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('ans:entities.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="ans:entities.explainer" />

      <div className="flex justify-end">
        <ExportButton path={`/api/v1/ans/export/entities?${ansEntityParams(filters).toString()}`} />
      </div>

      {data ? <AsOfStrip asOf={data.as_of} /> : null}

      <form
        className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-3"
        onSubmit={(event) => {
          event.preventDefault();
          setQuery(draft.trim() || null);
        }}
      >
        <div className="min-w-[16rem] flex-1 space-y-1">
          <label htmlFor="ans-entity-query" className="text-xs font-medium text-foreground">
            {t('ans:entities.search.label')}
          </label>
          <input
            id="ans-entity-query"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('ans:entities.search.placeholder')}
            className="w-full rounded-md border bg-background px-3 py-1.5 text-sm text-foreground"
          />
        </div>
        <label className="flex items-center gap-2 text-xs text-foreground">
          <input
            type="checkbox"
            checked={onlyPenalised}
            onChange={(event) => setOnlyPenalised(event.target.checked)}
          />
          {t('ans:entities.search.onlyPenalised')}
        </label>
        <button
          type="submit"
          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground"
        >
          {t('ans:entities.search.submit')}
        </button>
      </form>

      {data ? (
        <section className="rounded-lg border bg-card p-4" data-directory>
          <h2 className="mb-2 text-sm font-semibold text-foreground">
            {t('ans:entities.shape.title')}
          </h2>
          <Answer answer={data.directory} help={t('ans:entities.shape.help')}>
            {(d) => (
              <dl className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
                {(
                  [
                    ['entities', number.format(d.entities)],
                    ['withPenalties', number.format(d.with_penalties)],
                    ['withTwoOrMore', number.format(d.with_two_or_more)],
                    [
                      'registryOnly',
                      `${number.format(d.registry_only)} (${percent.format(d.registry_only_share)})`,
                    ],
                    ['active', number.format(d.active_registrations)],
                    ['cancelled', number.format(d.cancelled_registrations)],
                    ['withRegime', number.format(d.with_special_regime)],
                  ] as const
                ).map(([key, value]) => (
                  <div key={key} className="rounded-md border bg-muted/30 p-2" data-shape={key}>
                    <dt className="text-xs text-muted-foreground">
                      {t(`ans:entities.shape.${key}`)}
                    </dt>
                    <dd className="font-semibold tabular-nums text-foreground">{value}</dd>
                  </div>
                ))}
              </dl>
            )}
          </Answer>
          <div className="mt-3">
            <FieldGuide i18nKey="ans:entities.guide" />
          </div>
        </section>
      ) : null}

      {isPending ? (
        <SkeletonLoader rows={8} />
      ) : error ? (
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      ) : items.length === 0 ? (
        <EmptyState hint={t('ans:entities.empty')} />
      ) : (
        <section className="rounded-lg border bg-card" data-stale={isPlaceholderData || undefined}>
          <div className="px-4 py-2 text-xs text-muted-foreground" data-total={data?.total}>
            {t('ans:entities.count', {
              loaded: number.format(items.length),
              total: number.format(data?.total ?? 0),
            })}
          </div>
          <div ref={scrollRef} className="max-h-[70vh] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-muted text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">{t('ans:entities.columns.name')}</th>
                  <th className="px-3 py-2 font-medium">{t('ans:entities.columns.cnpj')}</th>
                  <th className="px-3 py-2 font-medium">{t('ans:entities.columns.registros')}</th>
                  <th className="px-3 py-2 font-medium">{t('ans:entities.columns.modalidade')}</th>
                  <th className="px-3 py-2 font-medium">{t('ans:entities.columns.status')}</th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('ans:entities.columns.cases')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('ans:entities.columns.reviewed')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('ans:entities.columns.regimes')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr
                    key={`${row.cnpj ?? row.name}`}
                    className="border-t align-top"
                    data-cnpj={row.cnpj}
                  >
                    <td className="px-3 py-2">
                      {row.cnpj ? (
                        <Link
                          to={`/ans/e/${row.cnpj}`}
                          className="text-primary underline-offset-4 hover:underline"
                        >
                          {row.name}
                        </Link>
                      ) : (
                        <span className="text-foreground">{row.name}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 tabular-nums text-muted-foreground">
                      {row.cnpj ?? '—'}
                    </td>
                    <td className="px-3 py-2 tabular-nums text-muted-foreground">
                      {row.registros.length ? row.registros.join(', ') : '—'}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {row.modalidade ?? '—'}
                      {row.uf ? <span className="ml-1 text-xs">({row.uf})</span> : null}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {row.registry_status
                        ? t(`ans:entities.registryStatus.${row.registry_status}`, {
                            defaultValue: row.registry_status,
                          })
                        : t('ans:entities.registryStatus.none')}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-foreground">
                      {row.cases === 0 ? (
                        <span className="italic text-muted-foreground">
                          {t('ans:entities.noPenalty')}
                        </span>
                      ) : (
                        number.format(row.cases)
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {number.format(row.reviewed)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {number.format(row.special_regimes)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div ref={sentinelRef} />
          </div>
          {hasNextPage ? (
            <div className="border-t px-4 py-2 text-center">
              <button
                type="button"
                onClick={loadMore}
                disabled={isFetchingNextPage}
                className="text-sm text-primary underline-offset-2 hover:underline"
              >
                {isFetchingNextPage ? t('common:state.loading') : t('ans:entities.loadMore')}
              </button>
            </div>
          ) : null}
        </section>
      )}
    </div>
  );
}
